import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';

import type { SpaceOverlayModel } from '@toolproof-core/visualization/runtime/_contract/overlay';

import { ACTION_FREE, ACTION_OVERVIEW } from '@/workflow/_pure/derivation/overviewNavigation';
import { SECTION_NATURAL_INPUT } from '@/workflow-builder/_data/overlay';
import {
    ACTION_ADD_BRANCH_CASE,
    ACTION_BEGIN_BRANCH_DEFAULT_CASE,
    ACTION_BIND_BOOLEAN_FALSE,
    ACTION_BIND_BOOLEAN_TRUE,
    ACTION_BIND_EXTERNAL,
    ACTION_BIND_NATURAL,
    ACTION_COMMIT_WORKFLOW,
    ACTION_CREATE_DRAFT_PREFIX,
    ACTION_DISCARD_DRAFT,
    ACTION_DISCARD_WORKFLOW,
    ACTION_DISPATCH_WORKFLOW,
    ACTION_FINALIZE_DRAFT,
    ACTION_FINALIZE_TOOL_BINDINGS,
    ACTION_RUN_REMOTE,
    ACTION_RUN_WORKFLOW,
    ACTION_SUBMIT_RUNTIME_FALSE,
    ACTION_SUBMIT_RUNTIME_NATURAL,
    ACTION_SUBMIT_RUNTIME_TRUE,
    SECTION_RUNTIME_INPUT,
} from '@/workflow-builder/_pure/interaction/actionMapping';
import {
    deriveSelectedWorkflowStateEntry,
    selectBindingTargetFromSelection,
} from '@/workflow-builder/_pure/interaction/selection';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

export function buildOverlayModel(args: {
    config: Config;
    runtimeState: RuntimeState;
    cosmosData: CosmosDataModel;
}): SpaceOverlayModel | undefined {
    if (args.config.adapterMode !== 'builder') return undefined;

    const selectedBindingTarget = selectBindingTargetFromSelection({
        runtimeState: args.runtimeState,
        cosmosData: args.cosmosData,
    });
    const selectedToolStep = args.runtimeState.selectionState.selectedToolStep;
    const selectedRoleName = args.runtimeState.selectionState.selectedInputRoleName;
    const selectedToolName = selectedToolStep ? args.cosmosData.toolMap.get(selectedToolStep.toolHandle)?.name : undefined;
    const phaseLabels: Record<RuntimeState['phase'], string> = {
        idle: 'Ready to build',
        selecting_tool_at_slot: 'Selecting tool',
        binding_inputs: 'Binding inputs',
        reviewing_input_bindings: 'Reviewing input bindings',
        reviewing_branch_cases: 'Reviewing branch cases',
        workflow_ready: 'Workflow ready',
    };
    const draftLabels = { tool: 'Tool', branch: 'Branch', for: 'For loop', while: 'While loop' };
    const executionLabels = { idle: 'Idle', running: 'Running', failed: 'Failed' };
    const errorMessage = args.runtimeState.error?.message
        ?? (args.runtimeState.executionState.executionStateKind === 'failed' ? args.runtimeState.executionState.message : undefined);
    const selectedWorkflowStateEntry = deriveSelectedWorkflowStateEntry(args.runtimeState);
    const selectedBooleanProjection = selectedWorkflowStateEntry?.workflowStateEntryKind === 'materializedResource'
        && typeof selectedWorkflowStateEntry.projection === 'boolean'
        ? selectedWorkflowStateEntry.projection
        : null;
    const isRunning = args.runtimeState.executionState.executionStateKind === 'running';
    const localInput = args.runtimeState.executionState.executionStateKind === 'running'
        ? args.runtimeState.executionState.localInput : undefined;
    const canCreateDraft = (args.runtimeState.phase === 'idle' || args.runtimeState.phase === 'workflow_ready')
        && !isRunning;
    const activeDraftKind = args.runtimeState.draft?.draftKind ?? null;
    const isBindingPhase = args.runtimeState.phase === 'binding_inputs' || args.runtimeState.phase === 'reviewing_input_bindings';
    const showBooleanBindingActions = isBindingPhase
        && selectedBindingTarget?.resourceTypeHandle === CONSTANTS.Handles.ResourceTypes.TYPE_Boolean;
    const showNaturalBindingAction = isBindingPhase
        && selectedBindingTarget?.resourceTypeHandle === CONSTANTS.Handles.ResourceTypes.TYPE_Natural;
    const showNaturalInput = showNaturalBindingAction;
    const showFinalizeToolBindings = args.runtimeState.phase === 'reviewing_input_bindings';
    const showBeginBranchDefaultCase = activeDraftKind === 'branch'
        && args.runtimeState.phase === 'reviewing_branch_cases';
    const showExecutionActions = args.runtimeState.phase === 'workflow_ready' && args.runtimeState.draft === null;
    const isCommitActive = args.runtimeState.phase === 'workflow_ready'
        && args.runtimeState.executionState.executionStateKind === 'idle';
    const sections: SpaceOverlayModel['sections'] = [
        {
            sectionKind: 'actions',
            sectionId: 'camera-navigation',
            label: 'Camera',
            actions: [
                { actionId: ACTION_OVERVIEW, label: 'Overview', enabled: !args.runtimeState.pendingOverviewNavigation },
                { actionId: ACTION_FREE, label: 'Resume free navigation', enabled: !!args.runtimeState.pendingOverviewNavigation },
            ],
        },
        {
            sectionKind: 'actions' as const,
            sectionId: 'builder-create-draft-actions',
            label: 'Create Draft',
            actions: [
                {
                    actionId: `${ACTION_CREATE_DRAFT_PREFIX}tool`,
                    label: 'Build ToolStep',
                    enabled: canCreateDraft,
                    isActive: activeDraftKind === 'tool',
                },
                {
                    actionId: `${ACTION_CREATE_DRAFT_PREFIX}branch`,
                    label: 'Build BranchStep',
                    enabled: canCreateDraft,
                    isActive: activeDraftKind === 'branch',
                },
                {
                    actionId: `${ACTION_CREATE_DRAFT_PREFIX}for`,
                    label: 'Build ForStep',
                    enabled: canCreateDraft,
                    isActive: activeDraftKind === 'for',
                },
                {
                    actionId: `${ACTION_CREATE_DRAFT_PREFIX}while`,
                    label: 'Build WhileStep',
                    enabled: canCreateDraft,
                    isActive: activeDraftKind === 'while',
                },
            ],
        },
        ...(showBooleanBindingActions
            ? [{
                sectionKind: 'actions' as const,
                sectionId: 'builder-binding-actions',
                label: 'Binding Commands',
                actions: [
                    { actionId: ACTION_BIND_BOOLEAN_TRUE, label: 'Bind true', enabled: true, isActive: selectedBooleanProjection === true },
                    { actionId: ACTION_BIND_BOOLEAN_FALSE, label: 'Bind false', enabled: true, isActive: selectedBooleanProjection === false },
                ],
            }]
            : []),
        ...(showNaturalInput
            ? [{
                sectionKind: 'text-input' as const,
                sectionId: SECTION_NATURAL_INPUT,
                label: 'Natural Input',
                value: args.runtimeState.naturalBindingInputText,
                placeholder: '0',
                description: 'Enter a non-negative integer then trigger Bind natural.',
                enabled: true,
            }]
            : []),
        ...(showNaturalBindingAction
            ? [{
                sectionKind: 'actions' as const,
                sectionId: 'builder-binding-actions',
                label: 'Binding Commands',
                actions: [
                    { actionId: ACTION_BIND_NATURAL, label: 'Bind natural', enabled: true },
                ],
            }]
            : []),
        ...(showFinalizeToolBindings
            ? [{
                sectionKind: 'actions' as const,
                sectionId: 'builder-finalize-tool-bindings-actions',
                label: 'Finalize Tool Bindings',
                actions: [
                    { actionId: ACTION_FINALIZE_TOOL_BINDINGS, label: 'Finalize Tool Bindings', enabled: true },
                ],
            }]
            : []),
        ...(isBindingPhase && selectedBindingTarget && !isRunning ? [{
            sectionKind: 'actions' as const,
            sectionId: 'builder-runtime-binding',
            label: 'Runtime Binding',
            actions: [{ actionId: ACTION_BIND_EXTERNAL, label: 'Provide at runtime', enabled: true }],
        }] : []),
        ...(localInput ? [
            {
                sectionKind: 'details' as const,
                sectionId: 'builder-runtime-requirement',
                label: 'Runtime Input Required',
                rows: [
                    { key: 'Input', value: localInput.requirement.target.roleName },
                    { key: 'Type', value: localInput.requirement.resourceTypeHandle === 'TYPE-Natural' ? 'Natural' : 'Boolean' },
                    { key: 'Status', value: localInput.submission ? 'Resuming...' : 'Waiting for input' },
                ],
            },
            ...(localInput.requirement.resourceTypeHandle === 'TYPE-Natural' ? [{
                sectionKind: 'text-input' as const, sectionId: SECTION_RUNTIME_INPUT,
                label: 'Runtime Natural Input', value: localInput.text, placeholder: '0',
                description: 'Enter a non-negative integer, then resume execution.',
                enabled: !localInput.submission,
            }] : []),
            {
                sectionKind: 'actions' as const, sectionId: 'builder-runtime-input-actions', label: 'Resume Execution',
                actions: localInput.requirement.resourceTypeHandle === 'TYPE-Natural'
                    ? [{ actionId: ACTION_SUBMIT_RUNTIME_NATURAL, label: 'Provide input and resume', enabled: !localInput.submission }]
                    : [
                        { actionId: ACTION_SUBMIT_RUNTIME_TRUE, label: 'Provide true', enabled: !localInput.submission },
                        { actionId: ACTION_SUBMIT_RUNTIME_FALSE, label: 'Provide false', enabled: !localInput.submission },
                    ],
            },
        ] : []),
    ];

    return {
        overlayId: 'workflow-builder',
        title: 'Workflow Builder',
        subtitle: 'Cursor-driven authoring flow',
        sections: [
            ...sections,
            {
                sectionKind: 'actions',
                sectionId: 'builder-draft-actions',
                label: 'Draft Commands',
                actions: [
                    { actionId: ACTION_ADD_BRANCH_CASE, label: 'Add Branch Case', enabled: args.runtimeState.phase === 'reviewing_branch_cases' },
                    ...(showBeginBranchDefaultCase
                        ? [{
                            actionId: ACTION_BEGIN_BRANCH_DEFAULT_CASE,
                            label: 'Begin Default Case',
                            enabled: args.runtimeState.draft?.draftKind === 'branch'
                                && args.runtimeState.draft.caseSetDraft.defaultCase === null,
                        }]
                        : [{ actionId: ACTION_FINALIZE_DRAFT, label: 'Finalize Draft', enabled: args.runtimeState.draft !== null }]),
                    { actionId: ACTION_DISCARD_DRAFT, label: 'Discard Draft', enabled: args.runtimeState.draft !== null },
                ],
            },
            {
                sectionKind: 'actions',
                sectionId: 'builder-workflow-actions',
                label: 'Workflow Commands',
                actions: [
                    {
                        actionId: ACTION_COMMIT_WORKFLOW,
                        label: 'Commit Workflow',
                        enabled: args.runtimeState.draft === null && !isRunning,
                        isActive: isCommitActive,
                    },
                    { actionId: ACTION_DISCARD_WORKFLOW, label: 'Discard Workflow', enabled: args.runtimeState.draft === null },
                ],
            },
            ...(showExecutionActions
                ? [{
                    sectionKind: 'actions' as const,
                    sectionId: 'builder-execution-actions',
                    label: 'Execution Commands',
                    actions: [
                        {
                            actionId: ACTION_RUN_WORKFLOW,
                            label: localInput ? 'Waiting for input...' : isRunning ? 'Running...' : 'Run locally',
                            enabled: !isRunning,
                            isActive: isRunning,
                        },
                        { actionId: ACTION_RUN_REMOTE, label: 'Run remotely', enabled: !isRunning },
                        { actionId: ACTION_DISPATCH_WORKFLOW, label: 'Dispatch', enabled: false },
                    ],
                }]
                : []),
            {
                sectionKind: 'details',
                sectionId: 'builder-status',
                label: 'State',
                rows: [
                    { key: 'Phase', value: phaseLabels[args.runtimeState.phase] },
                    ...(args.runtimeState.draft ? [{ key: 'Draft', value: draftLabels[args.runtimeState.draft.draftKind] }] : []),
                    ...(selectedToolName ? [{ key: 'Selected tool', value: selectedToolName }] : []),
                    ...(selectedRoleName ? [{ key: 'Selected input', value: selectedRoleName }] : []),
                    { key: 'Steps', value: String(args.runtimeState.mutableWorkflow.stepsByThreadIndex[0]?.length ?? 0) },
                    { key: 'Execution', value: localInput ? 'Waiting for input' : executionLabels[args.runtimeState.executionState.executionStateKind] },
                    ...(errorMessage ? [{ key: 'Error', value: errorMessage }] : []),
                ],
            },
        ],
    };
}
